import type { ITuningEndpoint, TuningCapabilities, TuningCommand, TuningReceipt, TuningTrialSnapshot, TuningWireMessage } from '@playcastdotio/PeerCore';

/** Guest setting RPC is distinct from fresh negotiation and reports completed receipts only. */
export class RemoteTuningEndpoint implements ITuningEndpoint {
  private capability: TuningCapabilities;
  private readonly pending = new Map<string, { target: TuningCommand['target']; resolve(receipt: TuningReceipt): void; timer: ReturnType<typeof setTimeout> }>();
  constructor(private readonly options: { capabilities: TuningCapabilities; snapshot(): TuningTrialSnapshot; send(message: TuningWireMessage): boolean }) {
    this.capability = options.capabilities;
  }
  capabilities(): TuningCapabilities { return structuredClone(this.capability); }
  update(capabilities: TuningCapabilities): void { this.capability = structuredClone(capabilities); }
  snapshot(): TuningTrialSnapshot { return this.options.snapshot(); }
  apply(command: TuningCommand): Promise<TuningReceipt> {
    if (this.pending.has(command.commandId)) return Promise.reject(new Error('guest-command-in-progress'));
    return new Promise(resolve => {
      const timer = setTimeout(() => { this.pending.delete(command.commandId); resolve(this.failure(command, 'guest-command-timeout')); }, 5_000);
      this.pending.set(command.commandId, { target: command.target, resolve, timer });
      if (!this.options.send({ type: 'stream-tuning', version: 1, kind: 'command', payload: command })) {
        clearTimeout(timer); this.pending.delete(command.commandId); resolve(this.failure(command, 'guest-control-unavailable'));
      }
    });
  }
  accept(receipt: TuningReceipt): boolean {
    const pending = this.pending.get(receipt.commandId);
    if (!pending || JSON.stringify(pending.target) !== JSON.stringify(receipt.target) || receipt.stage === 'accepted') return false;
    clearTimeout(pending.timer); this.pending.delete(receipt.commandId); pending.resolve(receipt); return true;
  }
  dispose(): void {
    for (const [commandId, pending] of this.pending) {
      clearTimeout(pending.timer); pending.resolve({ version: 1, commandId, target: pending.target, stage: 'failed',
        settings: [], policyVersion: 'tuning-v1', error: { code: 'endpoint-disposed', message: 'Guest endpoint disposed' } });
    }
    this.pending.clear();
  }
  private failure(command: TuningCommand, code: string): TuningReceipt {
    return { version: 1, commandId: command.commandId, target: command.target, stage: 'failed', settings: [],
      policyVersion: 'tuning-v1', error: { code, message: code } };
  }
}
